import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { AdvShell } from "@/components/AdvShell";
import { FeatureAdPreview } from "@/components/FeatureAdPreview";
import { money, num } from "./advUtils";
import { toast } from "sonner";
import { Upload, Sparkles, RefreshCw, X, Check, ChevronLeft, ChevronRight, Search, Plus, Users, AlertTriangle } from "lucide-react";

const field = "w-full bg-race-bg border border-race-line rounded-md px-3 py-2.5 text-sm text-race-txt outline-none focus:border-race-volt";
const label = "block text-xs uppercase tracking-wide text-race-sub mb-1.5";
const src = (u) => (u ? (u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`) : null);
const STEPS = ["Campaign", "Creative", "Audience & Budget", "Review & Submit"];
const todayISO = () => new Date().toISOString().slice(0, 10);
const SIZES = [
  { key: "single", label: "X1", w: 324, h: 77, note: "In-race strip" },
  { key: "double", label: "X2", w: 324, h: 132, note: "Lobby banner" },
  { key: "triple", label: "X3", w: 324, h: 209, note: "Calendar tile" },
];
const primaryOf = (cr = {}) => cr.double || cr.triple || cr.single || "";

export default function AdvCreateCampaign() {
  const { id } = useParams();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState(params.get("step") === "creative" && id ? 1 : 0);
  const [cid, setCid] = useState(id || null);
  const [f, setF] = useState({ name: "", click_url: "", start_date: todayISO(), end_date: "", image_url: "", creatives: {}, interests: [], budget_usd: "" });
  const [tax, setTax] = useState([]);
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [cpm, setCpm] = useState({ cpm_usd: 5, estimated_impressions: 0, general: true });
  const [busy, setBusy] = useState(false);
  const [genPrompt, setGenPrompt] = useState("");
  const [genBusy, setGenBusy] = useState(false);
  const [genStage, setGenStage] = useState("");
  const [sugg, setSugg] = useState([]);
  const [reach, setReach] = useState(null);
  const [refImg, setRefImg] = useState("");
  const [slotBusy, setSlotBusy] = useState("");
  const on = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  useEffect(() => {   // deep link from the takeover warning lands on the takeover slot
    if (params.get("step") !== "creative") return;
    const t = setTimeout(() => {
      const el = document.querySelector('[data-testid="adv-feature-block"]');
      if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.classList.add("ring-2", "ring-amber-500"); setTimeout(() => el.classList.remove("ring-2", "ring-amber-500"), 2500); }
    }, 900);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/ads/taxonomy").then((r) => setTax(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const q = (search || f.name || "").trim();
    const t = setTimeout(() => {
      api.get(`/adv/interest-suggestions?q=${encodeURIComponent(q)}&limit=8`)
        .then((r) => setSugg(r.data.suggestions || [])).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [search, f.name]);
  useEffect(() => {
    if (id) api.get(`/adv/campaigns/${id}`).then((r) => {
      const c = r.data;
      setF({ name: c.name || "", click_url: c.click_url || "", start_date: (c.start_date || todayISO()).slice(0, 10),
        end_date: (c.end_date || "").slice(0, 10), image_url: c.image_url || "", creatives: c.creatives || {},
        interests: c.interests || [], budget_usd: c.budget_usd || "" });
    }).catch(() => {});
  }, [id]);

  const refreshCpm = useCallback(() => {
    api.get(`/adv/cpm-estimate?tags=${encodeURIComponent(f.interests.join(","))}&budget=${f.budget_usd || 0}`)
      .then((r) => setCpm(r.data)).catch(() => {});
  }, [f.interests, f.budget_usd]);
  useEffect(() => { refreshCpm(); }, [refreshCpm]);
  useEffect(() => {
    api.get(`/adv/reach?tags=${encodeURIComponent(f.interests.join(","))}`)
      .then((r) => setReach(r.data)).catch(() => {});
  }, [f.interests]);

  const payload = () => ({ name: f.name, click_url: f.click_url,
    start_date: f.start_date ? new Date(f.start_date + "T00:00:00Z").toISOString() : null,
    end_date: f.end_date ? new Date(f.end_date + "T23:59:59Z").toISOString() : null,
    image_url: f.image_url, creatives: f.creatives, interests: f.interests,
    budget_usd: f.budget_usd ? Number(f.budget_usd) : null });

  const saveDraft = async () => {
    if (cid) { await api.patch(`/adv/campaigns/${cid}`, payload()); return cid; }
    const { data } = await api.post("/adv/campaigns", payload());
    setCid(data.id); return data.id;
  };

  const uploadSlot = async (key, file) => {
    if (!file || !file.type.startsWith("image/")) return toast.error("Choose an image file.");
    if (key === "feature") {
      const d = await new Promise((res) => { const im = new Image(); im.onload = () => res(im.naturalWidth / im.naturalHeight); im.onerror = () => res(null); im.src = URL.createObjectURL(file); });
      if (d && Math.abs(d - 1080 / 2340) > 0.06) return toast.error("The full-screen ad must be 1080×2340 (tall phone shape).");
    }
    setSlotBusy(key);
    try { const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/adv/creatives/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((s) => { const cr = { ...s.creatives, [key]: data.url }; return { ...s, creatives: cr, image_url: primaryOf(cr) }; });
      toast.success("Banner uploaded");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSlotBusy(""); }
  };
  const removeSlot = (key) => setF((s) => { const cr = { ...s.creatives }; delete cr[key]; return { ...s, creatives: cr, image_url: primaryOf(cr) }; });
  const uploadRef = async (file) => {
    if (!file || !file.type.startsWith("image/")) return toast.error("Choose an image file.");
    setGenBusy(true);
    try { const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/adv/creatives/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRefImg(data.url); toast.success("Reference image added");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setGenBusy(false); }
  };
  const generate = async () => {
    if (!genPrompt.trim()) return toast.error("Describe your ad first.");
    setGenBusy(true);
    try { const body = { prompt: genPrompt.trim(), format: "double" }; if (refImg) body.reference_image_url = refImg;
      const { data } = await api.post("/adv/creatives/generate", body);
      setF((s) => { const cr = { ...s.creatives, double: data.url }; return { ...s, creatives: cr, image_url: primaryOf(cr) }; });
      toast.success("Ad generated");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setGenBusy(false); }
  };
  const genFeature = async () => {
    if (!genPrompt.trim()) return toast.error("Describe your ad in the AI box below first.");
    setSlotBusy("feature");
    try { const body = { prompt: genPrompt.trim(), format: "feature" };
      const seed = refImg || primaryOf(f.creatives); if (seed) body.reference_image_url = seed;
      const { data } = await api.post("/adv/creatives/generate", body);
      setF((s) => ({ ...s, creatives: { ...s.creatives, feature: data.url } }));
      toast.success("Feature ad generated");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSlotBusy(""); }
  };
  const genAll = async () => {
    if (!genPrompt.trim()) return toast.error("Describe your ad first.");
    setGenBusy(true);
    try {
      let seed = refImg || ""; const cr = {};
      for (const fmt of ["single", "double", "triple", "feature"]) {
        setGenStage(fmt === "feature" ? "Feature" : SIZES.find((x) => x.key === fmt).label);
        const body = { prompt: genPrompt.trim(), format: fmt };
        if (seed) body.reference_image_url = seed;
        const { data } = await api.post("/adv/creatives/generate", body);
        cr[fmt] = data.url; if (!seed) seed = data.url;
      }
      setF((s) => { const merged = { ...s.creatives, ...cr }; return { ...s, creatives: merged, image_url: primaryOf(merged) }; });
      toast.success("Generated X1, X2, X3 + Feature ad");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setGenBusy(false); setGenStage(""); }
  };

  const toggleInterest = (t) => setF((s) => ({ ...s, interests: s.interests.includes(t) ? s.interests.filter((x) => x !== t) : [...s.interests, t] }));

  const canNext = () => {
    if (step === 0) return f.name.trim() && f.click_url.trim() && f.start_date && f.end_date && f.end_date >= f.start_date;
    if (step === 1) return Object.values(f.creatives || {}).some(Boolean) || !!f.image_url;
    if (step === 2) return Number(f.budget_usd) > 0;
    return true;
  };

  const next = async () => {
    if (!canNext()) { toast.error("Please complete this step."); return; }
    setBusy(true);
    try { await saveDraft(); setStep((s) => Math.min(3, s + 1)); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const savedId = await saveDraft();
      await api.post(`/adv/campaigns/${savedId}/submit`);
      toast.success("Campaign submitted for review");
      nav("/advertiser/campaigns");
    } catch (e) {
      const status = e.response?.status;
      if (status === 402) { toast.error("Add a payment method to submit."); nav("/advertiser/billing"); }
      else toast.error(apiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const saveAndExit = async () => { setBusy(true); try { await saveDraft(); toast.success("Draft saved"); nav("/advertiser/campaigns"); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); } };

  const flatTax = tax.flatMap((c) => c.tags.map((t) => ({ tag: t, cat: c.category })));
  const filtered = search ? flatTax.filter((x) => x.tag.toLowerCase().includes(search.toLowerCase())) : flatTax.slice(0, showAll ? flatTax.length : 12);

  return (
    <AdvShell>
      <button onClick={() => nav("/advertiser/campaigns")} className="text-sm text-race-sub hover:text-race-txt mb-3 inline-flex items-center gap-1"><ChevronLeft size={15} /> Campaigns</button>
      <h1 className="font-heading text-4xl tracking-tight mb-5" data-testid="adv-wizard-title">{id ? "Edit Campaign" : "Create Campaign"}</h1>

      {/* stepper */}
      <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-1" data-testid="adv-stepper">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <button onClick={() => i < step && setStep(i)} className={`flex items-center gap-2 shrink-0 ${i <= step ? "text-race-txt" : "text-race-muted"}`}>
              <span className={`w-6 h-6 rounded-full grid place-items-center text-xs font-bold ${i < step ? "bg-race-volt text-race-bg" : i === step ? "bg-race-txt text-race-bg" : "bg-race-line/40 text-race-muted"}`}>{i < step ? <Check size={13} /> : i + 1}</span>
              <span className="text-sm font-medium whitespace-nowrap">{s}</span>
            </button>
            {i < STEPS.length - 1 && <div className={`h-px w-8 shrink-0 ${i < step ? "bg-race-volt" : "bg-race-line"}`} />}
          </React.Fragment>
        ))}
      </div>

      <div className="bg-race-surface border border-race-line rounded-lg p-6 max-w-3xl">
        {step === 0 && (
          <div data-testid="adv-step-campaign">
            <h2 className="font-heading text-2xl tracking-tight mb-4">Tell us about your campaign</h2>
            <div className="mb-4"><label className={label}>Campaign name</label><input className={field} value={f.name} onChange={on("name")} placeholder="Fall Football Campaign" data-testid="adv-f-name" /></div>
            <div className="mb-4"><label className={label}>Click-through URL</label><input className={field} value={f.click_url} onChange={on("click_url")} placeholder="https://example.com/offer" data-testid="adv-f-url" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className={label}>Start date</label><input type="date" min={todayISO()} className={field} value={f.start_date} onChange={on("start_date")} data-testid="adv-f-start" /></div>
              <div><label className={label}>End date</label><input type="date" min={f.start_date || todayISO()} className={field} value={f.end_date} onChange={on("end_date")} data-testid="adv-f-end" /></div>
            </div>
            <p className="text-xs text-race-muted mt-2">Times are in UTC. Your ad serves between these dates.</p>
          </div>
        )}

        {step === 1 && (
          <div data-testid="adv-step-creative">
            <h2 className="font-heading text-2xl tracking-tight mb-1">Create your ad</h2>
            <p className="text-race-sub text-sm mb-5">Upload your own banners for each size, or describe your ad (optionally with a reference image) and let AI design all three.</p>

            {/* Path A — upload your own 3 banners */}
            <div className="flex items-center justify-between mb-3">
              <label className={label + " !mb-0"}>Upload your own banners</label>
              <span className="text-xs text-race-muted">{SIZES.filter((z) => f.creatives?.[z.key]).length} of 3 sizes added</span>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              {SIZES.map((z) => {
                const url = f.creatives?.[z.key];
                return (
                  <div key={z.key} data-testid={`adv-slot-${z.key}`}>
                    <div className="flex items-baseline justify-between mb-1.5">
                      <span className="text-xs font-semibold text-race-txt">{z.label} <span className="text-race-muted font-normal">{z.w}×{z.h}</span></span>
                      {url && <button onClick={() => removeSlot(z.key)} data-testid={`adv-slot-remove-${z.key}`} className="text-race-muted hover:text-race-red"><X size={13} /></button>}
                    </div>
                    {url ? (
                      <label className="block rounded-md border border-race-line overflow-hidden bg-race-bg cursor-pointer group relative" style={{ aspectRatio: `${z.w} / ${z.h}` }}>
                        <img src={src(url)} alt={z.label} className="w-full h-full object-cover" data-testid={`adv-slot-preview-${z.key}`} />
                        <span className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 grid place-items-center text-xs font-semibold text-white transition-opacity">Replace</span>
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadSlot(z.key, e.target.files?.[0])} />
                      </label>
                    ) : (
                      <label onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("border-race-volt", "text-race-volt"); }}
                        onDragLeave={(e) => e.currentTarget.classList.remove("border-race-volt", "text-race-volt")}
                        onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove("border-race-volt", "text-race-volt"); uploadSlot(z.key, e.dataTransfer.files?.[0]); }}
                        className="flex flex-col items-center justify-center text-center rounded-md border border-dashed border-race-line text-race-sub hover:border-race-volt hover:text-race-volt cursor-pointer transition-colors" style={{ aspectRatio: `${z.w} / ${z.h}` }}
                        data-testid={`adv-slot-upload-${z.key}`}>
                        {slotBusy === z.key ? <RefreshCw size={16} className="animate-spin" /> : <Upload size={16} />}
                        <span className="text-[10px] mt-1">{z.note}</span>
                        <input type="file" accept="image/*" className="hidden" data-testid={`adv-slot-file-${z.key}`} onChange={(e) => uploadSlot(z.key, e.target.files?.[0])} />
                      </label>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-[11px] text-race-muted mt-2">PNG or JPG. Add at least one size — we'll auto-fit the others from what you provide.</p>

            {/* Premium countdown-feature creative — separate portrait format */}
            <div className="mt-6 border border-race-volt/40 bg-race-volt/5 rounded-md p-4" data-testid="adv-feature-block">
              <div className="flex items-baseline justify-between mb-1">
                <span className="text-sm font-semibold text-race-txt">Countdown Takeover Ad <span className="text-[10px] uppercase tracking-widest text-race-volt border border-race-volt/50 rounded px-1.5 py-0.5 ml-1">Premium</span></span>
                <span className="text-xs text-race-muted">{f.creatives?.feature ? "1 added" : "optional"}</span>
              </div>
              <p className="text-[11px] text-race-muted mb-3">A <strong>full-screen phone takeover</strong> shown behind the race card during the <strong>final 10-second countdown</strong>. Size <strong>1080 × 2340</strong>: brand logo in the top 14%, keep 14–58% plain (the race card and Live Positions cover it), and put your message in the bottom 42%. Use "Check my design" on the preview to see what will be hidden. Display-only — no buttons, links or calls-to-action, so racers stay focused.</p>
              <div className="flex flex-col sm:flex-row gap-6 items-start">
              <div className="w-[150px] shrink-0">
                <div className="flex items-baseline justify-between mb-1.5">
                  <span className="text-xs font-semibold text-race-txt">Feature <span className="text-race-muted font-normal">1080×2340</span></span>
                  {f.creatives?.feature && <button onClick={() => removeSlot("feature")} data-testid="adv-slot-remove-feature" className="text-race-muted hover:text-race-red"><X size={13} /></button>}
                </div>
                {f.creatives?.feature ? (
                  <label className="block rounded-md border border-race-line overflow-hidden bg-race-bg cursor-pointer group relative" style={{ aspectRatio: "1080 / 2340" }}>
                    <img src={src(f.creatives.feature)} alt="Feature" className="w-full h-full object-contain" data-testid="adv-slot-preview-feature" />
                    <span className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 grid place-items-center text-xs font-semibold text-white transition-opacity">Replace</span>
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadSlot("feature", e.target.files?.[0])} />
                  </label>
                ) : (
                  <label onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("border-race-volt", "text-race-volt"); }}
                    onDragLeave={(e) => e.currentTarget.classList.remove("border-race-volt", "text-race-volt")}
                    onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove("border-race-volt", "text-race-volt"); uploadSlot("feature", e.dataTransfer.files?.[0]); }}
                    className="flex flex-col items-center justify-center text-center rounded-md border border-dashed border-race-line text-race-sub hover:border-race-volt hover:text-race-volt cursor-pointer transition-colors" style={{ aspectRatio: "1080 / 2340" }}
                    data-testid="adv-slot-upload-feature">
                    {slotBusy === "feature" ? <RefreshCw size={16} className="animate-spin" /> : <Upload size={16} />}
                    <span className="text-[10px] mt-1">Full screen<br />1080×2340</span>
                    <input type="file" accept="image/*" className="hidden" data-testid="adv-slot-file-feature" onChange={(e) => uploadSlot("feature", e.target.files?.[0])} />
                  </label>
                )}
                <button onClick={genFeature} disabled={genBusy || !!slotBusy} data-testid="adv-gen-feature"
                  className="mt-2 w-full inline-flex items-center justify-center gap-1.5 border border-race-volt/50 text-race-volt rounded-md py-1.5 text-xs font-semibold hover:bg-race-volt/10 disabled:opacity-50">
                  {slotBusy === "feature" ? <RefreshCw size={12} className="animate-spin" /> : <Sparkles size={12} />}
                  {slotBusy === "feature" ? "Designing…" : f.creatives?.feature ? "Regenerate with AI" : "Generate with AI"}
                </button>
              </div>
              <FeatureAdPreview src={src(f.creatives?.feature)} />
              </div>
            </div>

            <div className="flex items-center gap-3 my-6"><div className="h-px flex-1 bg-race-line" /><span className="text-xs uppercase tracking-wide text-race-muted">or create with AI</span><div className="h-px flex-1 bg-race-line" /></div>

            {/* Path B — AI generator with optional reference image */}
            <div className="border border-race-volt/40 bg-race-volt/5 rounded-md p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-race-volt mb-2"><Sparkles size={15} /> Create an ad with AI</div>
              <div className="grid md:grid-cols-3 gap-3">
                <div className="md:col-span-2">
                  <textarea rows={4} value={genPrompt} onChange={(e) => setGenPrompt(e.target.value)} data-testid="adv-gen-prompt"
                    placeholder="e.g. Bold banner for a coffee brand, warm colors, 'Order Now' button" className={`${field} resize-none h-full`} />
                </div>
                <div>
                  <input id="adv-ref-file" type="file" accept="image/*" className="hidden" data-testid="adv-gen-ref-file" onChange={(e) => uploadRef(e.target.files?.[0])} />
                  {refImg ? (
                    <div className="relative rounded-md border border-race-line overflow-hidden bg-race-bg h-full min-h-[92px]" data-testid="adv-gen-ref-preview">
                      <img src={src(refImg)} alt="reference" className="w-full h-full object-cover" />
                      <button onClick={() => setRefImg("")} data-testid="adv-gen-ref-remove" className="absolute top-1 right-1 w-6 h-6 grid place-items-center rounded-full bg-black/60 text-white hover:bg-race-red"><X size={12} /></button>
                    </div>
                  ) : (
                    <label htmlFor="adv-ref-file" data-testid="adv-gen-ref-btn"
                      onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("border-race-volt", "text-race-volt"); }}
                      onDragLeave={(e) => e.currentTarget.classList.remove("border-race-volt", "text-race-volt")}
                      onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove("border-race-volt", "text-race-volt"); uploadRef(e.dataTransfer.files?.[0]); }}
                      className="flex flex-col items-center justify-center text-center rounded-md border border-dashed border-race-line text-race-sub hover:border-race-volt hover:text-race-volt cursor-pointer transition-colors h-full min-h-[92px] p-2">
                      <Upload size={15} />
                      <span className="text-[10px] mt-1 leading-tight">Reference image<br />(logo / product · optional)</span>
                    </label>
                  )}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 mt-3">
                <button onClick={generate} disabled={genBusy} data-testid="adv-gen-go"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-race-volt text-race-bg font-semibold rounded-md py-2 text-sm disabled:opacity-50">
                  {genBusy ? <RefreshCw size={14} className="animate-spin" /> : <Sparkles size={14} />} Generate Ad</button>
                <button onClick={genAll} disabled={genBusy} data-testid="adv-gen-all"
                  className="flex-1 inline-flex items-center justify-center gap-2 border border-race-volt/50 text-race-volt rounded-md py-2 text-sm font-semibold hover:bg-race-volt/10 disabled:opacity-50">
                  {genBusy ? <RefreshCw size={13} className="animate-spin" /> : <Sparkles size={13} />} Generate all sizes + Feature</button>
              </div>
              <p className="text-[10px] text-race-muted mt-2">A reference image keeps generated banners on-brand. "All sizes + Feature" fills every placement (in-race, lobby, calendar and the countdown Feature ad) with a consistent look.</p>
              {genBusy && <p className="text-[11px] text-race-volt mt-2 flex items-center gap-1.5"><RefreshCw size={11} className="animate-spin" /> {genStage ? `Designing ${genStage} (step ${["X1","X2","X3","Feature"].indexOf(genStage) + 1} of 4)…` : "Designing your ad…"}</p>}
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="adv-step-audience">
            <h2 className="font-heading text-2xl tracking-tight mb-1">Choose your audience</h2>
            <p className="text-race-sub text-sm mb-4">Leave interests blank to reach all eligible Text Racing users (General Audience).</p>
            <label className={label}>Personalization interests</label>
            <div className="relative mb-3">
              <Search size={15} className="absolute left-3 top-3 text-race-muted" />
              <input className={`${field} pl-9`} placeholder="Search interests…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="adv-interest-search" />
            </div>
            {sugg.filter((s) => !f.interests.includes(s.tag)).length > 0 && (
              <div className="mb-3" data-testid="adv-interest-suggestions">
                <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-race-volt mb-2"><Sparkles size={12} /> Suggested for you</div>
                <div className="flex flex-wrap gap-2">
                  {sugg.filter((s) => !f.interests.includes(s.tag)).slice(0, 8).map((s) => (
                    <button key={s.tag} onClick={() => toggleInterest(s.tag)} data-testid={`adv-suggest-${s.tag}`}
                      title={`${s.reach.toLocaleString()} racers · ${money(s.cpm_usd)} CPM`}
                      className="inline-flex items-center gap-1.5 text-xs pl-2.5 pr-2 py-1 rounded-full border border-race-volt/30 bg-race-volt/5 text-race-txt hover:border-race-volt/60 transition-colors">
                      <span>{s.tag}</span>
                      <span className="text-[10px] text-race-muted">{s.reach >= 1000 ? `${(s.reach / 1000).toFixed(1)}k` : s.reach} reach</span>
                      <Plus size={12} className="text-race-volt" />
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-race-muted mt-1.5">Ranked by how many Text Racing racers actually follow each interest.</p>
              </div>
            )}
            {f.interests.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3" data-testid="adv-selected-interests">
                {f.interests.map((t) => (
                  <button key={t} onClick={() => toggleInterest(t)} className="inline-flex items-center gap-1 bg-race-volt/15 border border-race-volt/40 text-race-volt text-xs px-2.5 py-1 rounded-full">{t} <X size={12} /></button>
                ))}
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {filtered.map((x) => {
                const on2 = f.interests.includes(x.tag);
                return <button key={x.tag} onClick={() => toggleInterest(x.tag)} data-testid={`adv-interest-${x.tag}`}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${on2 ? "bg-race-volt/15 border-race-volt/40 text-race-volt" : "border-race-line text-race-sub hover:text-race-txt"}`}>{x.tag}</button>;
              })}
            </div>
            {!search && flatTax.length > 12 && <button onClick={() => setShowAll((v) => !v)} className="text-xs text-race-volt hover:underline mt-3" data-testid="adv-view-all-interests">{showAll ? "Show fewer" : "View all interests"}</button>}

            <div className="mt-4 flex items-center gap-3 rounded-lg border border-race-line bg-race-bg px-4 py-3" data-testid="adv-reach-preview">
              <Users size={20} className="text-race-volt shrink-0" />
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-wide text-race-muted">Estimated reachable racers</div>
                <div className="font-heading text-2xl leading-tight" data-testid="adv-reach-value">
                  {reach == null ? "…" : `~${num(reach.reachable)}`}
                  <span className="text-xs font-normal text-race-sub ml-2">of {num(reach?.total ?? 0)} eligible</span>
                </div>
                <div className="text-xs text-race-sub">{reach?.general ? "General Audience — reaching all eligible Text Racing racers." : `${f.interests.length} interest${f.interests.length === 1 ? "" : "s"} selected — narrower, more relevant reach.`}</div>
              </div>
            </div>

            <div className="border-t border-race-line my-6" />
            <h3 className="font-heading text-xl tracking-tight mb-1">Set your budget</h3>
            <label className={label}>Campaign budget (maximum you'll spend)</label>
            <div className="relative max-w-xs">
              <span className="absolute left-3 top-2.5 text-race-sub">$</span>
              <input type="number" min="1" className={`${field} pl-7`} value={f.budget_usd} onChange={on("budget_usd")} placeholder="500" data-testid="adv-f-budget" />
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4 max-w-md">
              <div className="bg-race-bg border border-race-line rounded-md p-4"><div className="text-[11px] uppercase text-race-muted">Your CPM</div><div className="font-heading text-2xl mt-1">{money(cpm.cpm_usd)}</div><div className="text-xs text-race-sub">Cost per 1,000 impressions{cpm.general ? " · general" : ""}</div></div>
              <div className="bg-race-bg border border-race-line rounded-md p-4"><div className="text-[11px] uppercase text-race-muted">Estimated delivery</div><div className="font-heading text-2xl mt-1">~{num(cpm.estimated_impressions)}</div><div className="text-xs text-race-sub">impressions (estimate, not guaranteed)</div></div>
            </div>
            <p className="text-xs text-race-muted mt-3">You're billed weekly for what you actually deliver — never more than your budget.</p>
            {(() => {
              const estImpr = cpm.estimated_impressions || 0;
              const aud = reach?.general ? 0 : (reach?.reachable || 0);
              const budgetNum = parseFloat(f.budget_usd) || 0;
              if (!(budgetNum > 0 && aud > 0 && estImpr < aud)) return null;
              const suggested = Math.max(1, Math.ceil((aud * (cpm.cpm_usd || 5)) / 1000));
              return (
                <div className="mt-4 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 flex items-start gap-3" data-testid="adv-budget-warning">
                  <AlertTriangle size={18} className="text-amber-400 shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <div className="font-semibold text-amber-300">Your budget may be too small for this audience</div>
                    <div className="text-race-sub mt-0.5">At {money(cpm.cpm_usd)} CPM, ${budgetNum.toLocaleString()} delivers only ~{num(estImpr)} impressions — fewer than the ~{num(aud)} racers your personalization reaches, so many won't see your ad even once.</div>
                    <button type="button" onClick={() => setF((s) => ({ ...s, budget_usd: String(suggested) }))} data-testid="adv-budget-suggest"
                      className="mt-2 text-xs font-semibold text-amber-300 hover:underline">Set budget to {money(suggested)} to reach them all once →</button>
                    <span className="text-race-muted text-xs"> or add more interests to widen your audience.</span>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {step === 3 && (
          <div data-testid="adv-step-review">
            <h2 className="font-heading text-2xl tracking-tight mb-4">{f.name || "Your campaign"}</h2>
            <ReviewRow label="Campaign dates" onEdit={() => setStep(0)} value={`${f.start_date} → ${f.end_date}${f.start_date && f.end_date ? ` · ${Math.max(1, Math.round((new Date(f.end_date) - new Date(f.start_date)) / 86400000) + 1)} days` : ""}`} />
            <div className="py-3 border-b border-race-line flex justify-between gap-4">
              <div><div className="text-xs uppercase text-race-muted mb-1">Creative</div>{f.image_url && <img src={src(f.image_url)} alt="" className="w-56 rounded border border-race-line" />}</div>
              <button onClick={() => setStep(1)} className="text-xs text-race-volt hover:underline shrink-0">Edit creative</button>
            </div>
            {f.creatives?.feature && (
              <div className="py-3 border-b border-race-line flex justify-between gap-4" data-testid="adv-review-feature">
                <div><div className="text-xs uppercase text-race-muted mb-2">Countdown Feature Ad</div><FeatureAdPreview src={src(f.creatives.feature)} /></div>
                <button onClick={() => setStep(1)} className="text-xs text-race-volt hover:underline shrink-0">Edit creative</button>
              </div>
            )}
            <ReviewRow label="Destination" onEdit={() => setStep(0)} value={f.click_url || "—"} />
            <ReviewRow label="Audience" onEdit={() => setStep(2)} value={f.interests.length ? f.interests.join(", ") : "General Audience"} />
            <ReviewRow label="Budget" onEdit={() => setStep(2)} value={money(Number(f.budget_usd || 0))} />
            <ReviewRow label="CPM" value={money(cpm.cpm_usd)} />
            <ReviewRow label="Estimated impressions" value={`~${num(cpm.estimated_impressions)}`} last />
            <div className="mt-5 rounded-md border border-race-line bg-race-bg px-4 py-3 text-xs text-race-sub">
              Submitting sends this campaign to the Text Racing team for review. You'll be billed weekly for delivered spend once it's approved and running — a payment method is required.
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mt-8 pt-5 border-t border-race-line">
          <button onClick={() => step === 0 ? nav("/advertiser/campaigns") : setStep((s) => s - 1)} className="text-sm text-race-sub hover:text-race-txt inline-flex items-center gap-1"><ChevronLeft size={15} /> {step === 0 ? "Cancel" : "Back"}</button>
          <div className="flex items-center gap-3">
            <button onClick={saveAndExit} disabled={busy} className="text-sm text-race-sub hover:text-race-txt">Save draft</button>
            {step < 3
              ? <button onClick={next} disabled={busy} data-testid="adv-wizard-next" className="inline-flex items-center gap-1 bg-race-txt text-race-bg font-semibold text-sm px-5 py-2.5 rounded-md hover:opacity-90 disabled:opacity-50">Continue <ChevronRight size={15} /></button>
              : <button onClick={submit} disabled={busy} data-testid="adv-wizard-submit" className="bg-race-volt text-race-bg font-semibold text-sm px-6 py-2.5 rounded-md hover:opacity-90 disabled:opacity-50">Submit for Review</button>}
          </div>
        </div>
      </div>
    </AdvShell>
  );
}

const ReviewRow = ({ label, value, onEdit, last }) => (
  <div className={`py-3 flex justify-between items-start gap-4 ${last ? "" : "border-b border-race-line"}`}>
    <div><div className="text-xs uppercase text-race-muted mb-0.5">{label}</div><div className="text-sm text-race-txt">{value}</div></div>
    {onEdit && <button onClick={onEdit} className="text-xs text-race-volt hover:underline shrink-0">Edit</button>}
  </div>
);
